import type { TreeNodeKind } from "../model/types.js";

import styles from "./NodeIcon.module.css";

interface NodeIconProps {
  readonly kind: TreeNodeKind;
}

interface SymbolIconProps {
  readonly path: string;
  readonly className?: string | undefined;
}

const SYMBOL_CLASS_PATH =
  "M11.34 9.70998H12.05L14.72 7.04005V6.32997L13.38 5.00001H12.68L10.86 6.81007H5.86V5.56007L7.71999 3.70997V3L5.71998 1H5.00001L1 5.00001V5.70997L3 7.70998H3.71003L4.84998 6.56007V12.35L5.34998 12.85H10V13.37L11.33 14.71H12.04L14.7101 12.0401V11.33L13.37 10H12.67L10.81 11.85H5.81001V7.84999H10V8.32997L11.34 9.70998ZM13.0301 6.06007L13.66 6.68995L11.66 8.68996L11.0301 8.06007L13.0301 6.06007ZM13.0301 11.0601L13.66 11.69L11.66 13.69L11.0301 13.0601L13.0301 11.0601ZM3.34998 6.65004L2.06 5.34998L5.34998 2.06006L6.65004 3.34998L3.34998 6.65004Z";
const SYMBOL_FIELD_PATH =
  "M14.45 4.5L9.44995 2H8.55005L1.55005 5.5L1 6.39001V10.89L1.55005 11.79L6.55005 14.29H7.44995L14.45 10.79L15 9.89001V5.39001L14.45 4.5ZM6.44995 13.14L1.94995 10.89V7.17004L6.44995 9.17004V13.14ZM6.94995 8.33997L2.29004 6.22998L8.94995 2.89001L13.62 5.22998L6.94995 8.33997ZM13.95 9.89001L7.44995 13.14V9.20996L13.95 6.20996V9.89001Z";
const SYMBOL_METHOD_PATH =
  "M13.51 4L8.51001 1H7.51001L2.51001 4L2.02002 4.85999V10.86L2.51001 11.71L7.51001 14.71H8.51001L13.51 11.71L14 10.86V4.85999L13.51 4ZM7.51001 13.5601L3.01001 10.86V5.69995L7.51001 8.15002V13.5601ZM3.27002 4.69995L8.01001 1.85999L12.75 4.69995L8.01001 7.29004L3.27002 4.69995ZM13.01 10.86L8.51001 13.5601V8.15002L13.01 5.69995V10.86Z";
const SYMBOL_FILE_PATH =
  "M13.85 4.44L10.57 1.14L10.22 1H2.5L2 1.5V14.5L2.5 15H13.5L14 14.5V4.8L13.85 4.44ZM13 5H10V2L13 5ZM3 14V2H9V5.5L9.5 6H13V14H3Z";

function SymbolIcon({ path, className }: SymbolIconProps) {
  const iconClass =
    className === undefined
      ? `${styles.icon} ${styles.symbolIcon}`
      : `${styles.icon} ${styles.symbolIcon} ${className}`;

  return (
    <svg class={iconClass} viewBox="0 0 16 16" aria-hidden="true">
      <path d={path} fill="currentColor" />
    </svg>
  );
}

export function NodeIcon({ kind }: NodeIconProps) {
  switch (kind) {
    case "artifact":
      return (
        <svg class={styles.icon} viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2.5 2.5h7l4 4v7h-11z" />
          <path d="M9.5 2.5v4h4" />
          <path d="M5 9h6M5 11.5h4" />
        </svg>
      );
    case "directory":
      return (
        <svg class={styles.icon} viewBox="0 0 16 16" aria-hidden="true">
          <path d="M1.75 4h4l1.2 1.5h7.3v7.25H1.75z" />
          <path d="M1.75 4V2.75h4.5L7.5 4" />
        </svg>
      );
    case "class":
      return (
        <SymbolIcon path={SYMBOL_CLASS_PATH} className={styles.classIcon} />
      );
    case "field":
      return (
        <SymbolIcon path={SYMBOL_FIELD_PATH} className={styles.fieldIcon} />
      );
    case "constructor":
      return (
        <SymbolIcon
          path={SYMBOL_METHOD_PATH}
          className={styles.constructorIcon}
        />
      );
    case "method":
      return (
        <SymbolIcon path={SYMBOL_METHOD_PATH} className={styles.methodIcon} />
      );
    case "error":
      return (
        <svg
          class={`${styles.icon} ${styles.errorIcon}`}
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path d="M8 1.75 14.25 13H1.75z" />
          <path d="M8 5v4.25M8 11.5v.25" />
        </svg>
      );
    case "file":
      return <SymbolIcon path={SYMBOL_FILE_PATH} />;
  }
}
